import React from 'react';
import _ from 'underscore';
import { Picker, View } from 'react-native';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BodyText from '../../ui/BodyText';
import GlyphIcon, { CHEVRON_DOWN } from '../../ui/GlyphIcon';

import './Select.module.scss';

/**
 * Creates a styled select menu.
 *
 * @example
 *   const options = ['foo', 'bar', 'baz'];
 *
 *   const handleChange = (value) => {
 *     console.log('Value', value);
 *   };
 *
 *   <Select
 *     name="my-select"
 *     options={options}
 *     onChange={this.handleChange}
 *   />
 *
 * You can pass either an array (see example above) or an object of options.
 * @example
 *   const states = {
 *     AL: 'Alabama',
 *     AK: 'Alaska',
 *     AZ: 'Arizona',
 *     AR: 'Arkansas',
 *     CA: 'California',
 *   }
 *
 *   <Select
 *     options={states}
 *   />
 *
 * Select a value
 * --------------
 * @example
 *   <Select
 *     options={states}
 *     selected="CA"
 *   />
 *
 * Groups of options
 * -----------------
 * To display groups of options, pass in an object with a child array or object for each value:
 * @example
 *   const services = {
 *      'Style': {
 *        12: 'Blowout',
 *        21: 'Updo',
 *      },
 *      'Hair Color': {
 *        102: 'Bleach & Tone',
 *        211: 'Hair Tint',
 *      }
 *   }
 *
 *   <Select
 *     options={services}
 *   />
 */
export default class Select extends React.PureComponent {
  static propTypes = {
    /** The select options to display */
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.object,
    ]).isRequired,

    /** The name of the select form item. */
    name: PropTypes.string,

    /** Include an empty element at the top */
    hasEmptyItem: PropTypes.bool,

    /** Fired when the selected value changes. */
    onChange: PropTypes.func,

    /** The selected option, used in `getDerivedStateFromProps` to initialize state variable */
    // eslint-disable-next-line react/no-unused-prop-types
    selected: PropTypes.any,

    /** A class to apply to the rendered container. */
    className: PropTypes.string,

    /** The text to display when nothing is selected */
    placeholder: PropTypes.string,

    /** Additional style options to attach to the component */
    style: PropTypes.any,

    testID: PropTypes.string,
  };

  static defaultProps = {
    name: null,
    hasEmptyItem: false,
    selected: null,
    className: '',
    placeholder: null,
    onChange: () => {},
    style: undefined,
    testID: '',
  };

  static getDerivedStateFromProps(props) {
    return { selected: props.selected };
  }

  state = {
    selected: null,
    focused: false,
  };

  // Flat list of option values & text
  optionsMap = {};

  /**
   * The selection changed on the list
   *
   * @param {Event} evt
   */
  handleChange = itemValue => {
    this.setState({
      selected: itemValue,
      focused: false,
    });
    this.props.onChange(itemValue);
  };

  /**
   * The select menu is focused.
   */
  setFocused = () => {
    this.setState({ focused: true });
  };

  /**
   * The select menu lost focus.
   */
  setBlurred = () => {
    this.setState({ focused: false });
  };

  /**
   * Render the option lists.
   * @param {List} optList - A list or hash of options to build.
   */
  renderOptions(optList, prefix) {
    const optArray = Array.isArray(optList);

    // Create option objects for render
    const optObjs = Object.entries(optList).map(([key, item]) => {
      const value = (optArray) ? item : key; // when options are an object, the key is the value
      let content = item;
      let children = null;

      // Nested options
      if (item && typeof item === 'object') {
        children = content;
        content = value;
      } else if (typeof this.optionsMap[value] === 'undefined') {
        // Add option value & content to the flat options map
        this.optionsMap[value] = content;
      }

      const optObj = {
        value,
        children,
        text: content,
      };

      return optObj;
    });

    // Render options
    return _.flatten(
      (optObjs.map(option => (
        (option.children)
          ? (
            this.renderOptions(option.children, option.text)
          ) : (
            <Picker.Item
              key={option.value}
              value={option.value}
              label={`${prefix ? `${prefix}: ` : ''}${option.text}`}
            />
          )
      ))),
      true,
    );
  }

  render() {
    const {
      options,
      name,
      className,
      style,
      placeholder,
      hasEmptyItem,
      testID,
    } = this.props;
    const { selected, focused } = this.state;
    const value = (selected === null) ? undefined : selected;

    this.optionsMap = {};

    let renderedOptions = this.renderOptions(options);
    let displayText = (selected) ? this.optionsMap[selected] : null;
    if (!displayText) {
      displayText = placeholder || '';
    }

    if (hasEmptyItem) {
      renderedOptions = [
        <Picker.Item label="" value="" key="__empty__" />,
        ...renderedOptions,
      ];
    }

    return (
      <View
        styleName={classNames(
          'select-component',
          {
            focused,
          },
        )}
        className={className}
        style={style}
      >
        <Picker
          name={name}
          onValueChange={this.handleChange}
          onFocus={this.setFocused}
          onBlur={this.setBlurred}
          selectedValue={value}
          styleName="picker"
          testID={testID}
        >
          {renderedOptions}
        </Picker>
        <BodyText
          styleName={selected ? 'text' : 'disabled-text'}
          testID={`${testID}-selection-text`}
        >
          {displayText}
        </BodyText>
        <GlyphIcon content={CHEVRON_DOWN} />
      </View>
    );
  }
}
